/**
 * Airport Autocomplete Component
 *
 * Provides searchable dropdown for airport selection.
 * On selection, returns full airport data for auto-filling form fields.
 */

import { useState, useEffect, useCallback } from 'react'
import { Check, ChevronsUpDown, Loader2, Plane } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useAirportSearch } from '@/hooks/useAirportSearch'
import type { AirportAutocompleteItem } from '@/types/airports'

interface AirportAutocompleteProps {
  /** Current value (airport code) */
  value?: string
  /** Called when an airport is selected */
  onSelect: (airport: AirportAutocompleteItem | null) => void
  /** Placeholder text */
  placeholder?: string
  /** Disable the input */
  disabled?: boolean
  /** Additional class names */
  className?: string
}

export function AirportAutocomplete({
  value,
  onSelect,
  placeholder = 'Search airports...',
  disabled = false,
  className,
}: AirportAutocompleteProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')

  // Debounce the search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  // Fetch airports based on debounced query
  const { data: airports, isLoading, isFetching } = useAirportSearch(
    debouncedQuery,
    open && debouncedQuery.length >= 2
  )

  const handleSelect = useCallback(
    (airport: AirportAutocompleteItem) => {
      onSelect(airport)
      setOpen(false)
      setSearchQuery('')
    },
    [onSelect]
  )

  // Display value - show code if set, otherwise placeholder
  const displayValue = value || ''

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            !value && 'text-muted-foreground',
            className
          )}
        >
          <span className="flex items-center gap-2 truncate">
            {value && <Plane className="h-4 w-4 shrink-0" />}
            {displayValue || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Type airport code or city..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {/* Loading state */}
            {(isLoading || isFetching) && debouncedQuery.length >= 2 && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">
                  Searching...
                </span>
              </div>
            )}

            {/* Empty state */}
            {!isLoading &&
              !isFetching &&
              debouncedQuery.length >= 2 &&
              (!airports || airports.length === 0) && (
                <CommandEmpty>No airports found.</CommandEmpty>
              )}

            {/* Results */}
            {airports && airports.length > 0 && (
              <CommandGroup heading="Airports">
                {airports.map((airport) => (
                  <CommandItem
                    key={`${airport.code}-${airport.faaCode}`}
                    value={airport.code}
                    onSelect={() => handleSelect(airport)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === airport.code ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <div className="flex flex-col">
                      <span className="font-medium">
                        {airport.code}
                        {airport.faaCode !== airport.code && (
                          <span className="ml-1 text-muted-foreground">
                            ({airport.faaCode})
                          </span>
                        )}
                        {' - '}
                        {airport.name}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {airport.city}, {airport.state}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Prompt state */}
            {debouncedQuery.length < 2 && !isLoading && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Type at least 2 characters to search
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
